// URL-based locale routing: English lives at the root, Slovak under /sk.
// Only pages listed in SK_PATHS get a /sk twin — everything else stays English-only,
// so links from a Slovak page to an untranslated page fall back to the English URL.
// To translate another page: add its path here and add its sk strings.

export type SiteLanguage = 'en' | 'sk';

export const SK_PREFIX = '/sk';

export const SK_PATHS = new Set<string>([
  '/',
  '/about',
  '/content',
  '/how-it-works',
  '/solutions',
  '/solutions/retail-automation-suite',
  '/our-work/data',
  '/our-work/digital-cfo',
  '/our-work/retail-profit-management',
  '/our-work/data-engineering',
  '/our-work/context-engineering',
  '/tell-me-more',
  '/book-a-call',
  '/case-studies/altisport',
  '/case-studies/herohero',
  '/case-studies/alasans',
  '/case-studies/dcm',
  '/blog/the-problem-with-ecommerce',
]);

export const getLanguageFromPath = (pathname: string): SiteLanguage =>
  pathname === SK_PREFIX || pathname.startsWith(`${SK_PREFIX}/`) ? 'sk' : 'en';

// "/sk/about" -> "/about", "/sk" -> "/"
export const stripLocale = (pathname: string): string => {
  if (getLanguageFromPath(pathname) === 'en') return pathname;
  return pathname.slice(SK_PREFIX.length) || '/';
};

const normalize = (path: string) => (path.length > 1 ? path.replace(/\/+$/, '') : path);

export const hasSkVersion = (path: string): boolean => SK_PATHS.has(normalize(path));

// Turns an English internal path ("/about", "/#consulting", "/tell-me-more?x=1")
// into its Slovak twin when one exists. External/relative links pass through untouched.
export const localizePath = (to: string, language: SiteLanguage): string => {
  if (language !== 'sk' || !to.startsWith('/') || getLanguageFromPath(to) === 'sk') return to;
  const match = to.match(/^([^?#]*)(.*)$/);
  const path = match?.[1] || '/';
  const rest = match?.[2] || '';
  if (!hasSkVersion(path)) return to;
  return `${SK_PREFIX}${path === '/' ? '' : normalize(path)}${rest}`;
};
